import {
  ArrowDownIcon,
  ArrowUpIcon,
  EyeIcon,
  LightningIcon,
  MinusIcon,
} from '@phosphor-icons/react';
import { endOfDay, endOfHour, sub } from 'date-fns';
import { Badge } from '@logspot-platform/ui/badge';
import { EmptyState } from '@logspot-platform/ui/empty-state';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@logspot-platform/ui/tooltip';
import { AreaChart } from './charts/area-chart';
import { Widget } from './widget';
import { numberWithCommas } from './lib/number-with-commas';
import type { EventWidgetProps } from './types';

function getVariant(diff: number) {
  if (diff < 0) {
    return 'error';
  } else if (diff > 0) {
    return 'success';
  } else {
    return 'neutral';
  }
}

export const EventWidget = ({
  name,
  hide = false,
  data,
  dateRange,
  prevData,
  iconType = 'event',
  primaryColor,
  backgroundColor,
}: EventWidgetProps) => {
  const currentTotal =
    data?.reduce((a, b) => a + parseInt(b.count.toString()), 0) ?? 0;
  const prevTotal =
    prevData?.reduce((a, b) => a + parseInt(b.count.toString()), 0) ?? 0;
  const diff =
    prevTotal === 0 ? 100 : ((currentTotal - prevTotal) * 100) / prevTotal;
  const formattedDiff = Math.abs(diff).toFixed(0);
  const prevDateRange = {
    interval: dateRange.interval,
    startDate: sub(dateRange.startDate, dateRange.period || { days: 1 }),
    endDate: dateRange.interval.includes('days')
      ? endOfDay(dateRange.startDate)
      : endOfHour(dateRange.startDate),
  };

  if (hide) {
    return null;
  }

  return (
    <Widget
      backgroundColor={backgroundColor}
      Header={() => (
        <div className="flex items-center justify-between">
          <span className="flex items-center gap-2 text-xl font-medium">
            {iconType === 'view' ? (
              <EyeIcon className="text-primary" style={{ color: primaryColor }} />
            ) : (
              <LightningIcon
                className="text-primary"
                style={{ color: primaryColor }}
              />
            )}
            <span className="font-semibold">{name}</span>
          </span>
          <span className="flex items-center gap-2">
            {!!data && (
              <>
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <span className="text-3xl font-black leading-none">
                        {numberWithCommas(currentTotal)}
                      </span>
                    </TooltipTrigger>
                    <TooltipContent>Events</TooltipContent>
                  </Tooltip>
                </TooltipProvider>
                <Badge variant={getVariant(diff)}>
                  {diff < 0 && <ArrowDownIcon weight="bold" />}
                  {diff === 0 && <MinusIcon weight="bold" />}
                  {diff > 0 && <ArrowUpIcon weight="bold" />}
                  <span className="text-muted-foreground">
                    {formattedDiff}%
                  </span>
                </Badge>
              </>
            )}
          </span>
        </div>
      )}
    >
      <>
        {!data && <EmptyState headText="No Results Found" />}
        <AreaChart
          data={data}
          dateRange={dateRange}
          prevData={prevData}
          prevDateRange={prevDateRange}
          primaryColor={primaryColor}
        />
      </>
    </Widget>
  );
};
